// See https://www.xe.com/symbols.php and https://en.wikipedia.org/wiki/ISO_4217#Active_codes
export enum CurrencySymbol {
  EUR = '€',
  BRL = 'R$',
  MXN = '$',
  USD = '$',
  GBP = '£',
  CHF = 'CHF',
}

/**
 * ISO 4217 CURRENCY CODES as specified in the documentation
 * Taken from https://www.iso.org/iso-4217-currency-codes.html
 * sorted and parsed
 */
type CurrencyIsoCode = 'EUR' | 'BRL' | 'MXN' | 'GBP' | 'USD' | 'CHF'

export interface Currency {
  isoCode: CurrencyIsoCode
  symbol: CurrencySymbol | string // We want to use this symbol once we centralize and remove Dinero.js dependency
  placeholder: string
  defaultLocale: string
}

const defineCurrency = (
  isoCode: CurrencyIsoCode,
  symbol: CurrencySymbol,
  defaultLocale: string,
  placeholder = '0.00'
) => {
  return {
    isoCode,
    symbol,
    defaultLocale, // This will be used as fallback locale when format money strings
    placeholder,
  }
}

export const EUR: Currency = defineCurrency('EUR', CurrencySymbol.EUR, 'es-es')

const BRL: Currency = defineCurrency('BRL', CurrencySymbol.BRL, 'pt-BR')

const MXN: Currency = defineCurrency('MXN', CurrencySymbol.MXN, 'es-MX')

export const USD: Currency = defineCurrency('USD', CurrencySymbol.USD, 'en-us')

export const GBP: Currency = defineCurrency('GBP', CurrencySymbol.GBP, 'en-gb')

const CHF: Currency = defineCurrency('CHF', CurrencySymbol.CHF, 'de-CH')

function isValidCurrencyIsoCode(code: string): code is CurrencyIsoCode {
  return ['EUR', 'BRL', 'MXN', 'GBP', 'USD', 'CHF'].includes(code)
}

// Define the currency map with known currencies
const CURRENCY_MAP = {
  EUR: EUR,
  BRL: BRL,
  MXN: MXN,
  USD: USD,
  GBP: GBP,
  CHF: CHF,
}

/**
 * Get a currency by its ISO code
 * If the ISO code is not supported, a fallback currency will be created
 */
export function getCurrencyByIsoCode(isoCode: string): Currency {
  if (isValidCurrencyIsoCode(isoCode) && isoCode in CURRENCY_MAP) {
    return CURRENCY_MAP[isoCode]
  }

  const fallbackCurrency: Currency = {
    ...EUR,
    symbol: isoCode,
    // @ts-expect-error: we need to cast isoCode as String because there is not a place with proper
    // isoCodes for all countries and we want to maintain our own type without the need to expand it
    // with all isoCodes
    isoCode,
  }

  return fallbackCurrency
}
